import React, { useCallback, useRef } from 'react';
import styles from './IconButton.module.css';

/**
 * @param {import('react').ButtonHTMLAttributes<HTMLButtonElement> & {
 *   icon?: import('react').ReactNode,
 *   size?: 'small' | 'medium' | 'large',
 *   variant?: 'contained' | 'outlined' | 'text',
 *   color?: string,
 *   disableRipple?: boolean,
 * }} props
 * @param {import('react').Ref<HTMLButtonElement>} ref
 */
const IconButtonRender = ({
  icon,
  children,
  size,
  variant = 'text',
  color,
  disabled = false,
  disableRipple = false,
  onClick,
  onMouseDown,
  onMouseUp,
  type = 'button',
  title,
  style,
  className: externalClassName,
  ...rest
}, ref) => {
  const sizeClass = size === 'small' ? styles.small
                  : size === 'medium' ? styles.medium
                  : size === 'large' ? styles.large
                  : null;

  const variantClass = variant === 'contained' ? styles.contained
                     : variant === 'outlined'  ? styles.outlined
                     : styles.text;

  const className = [
    styles.iconButton,
    sizeClass,
    variantClass,
    externalClassName,
  ].filter(Boolean).join(' ');

  const rippleWrapperRef = useRef(null);

  const colorStyle = color ? (
    variant === 'contained'
      ? { background: color }
      : { color, borderColor: color }
  ) : {};

  const inlineStyle = { ...colorStyle, ...style };

  const spawnRipple = useCallback((button, clientX, clientY) => {
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius   = diameter / 2;
    const rect     = button.getBoundingClientRect();
    const ripple   = document.createElement('span');
    ripple.style.width  = ripple.style.height = `${diameter}px`;
    ripple.style.left   = `${(clientX ?? rect.left + rect.width  / 2) - rect.left - radius}px`;
    ripple.style.top    = `${(clientY ?? rect.top  + rect.height / 2) - rect.top  - radius}px`;
    ripple.className    = styles.ripple;
    (rippleWrapperRef.current ?? button).appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (!disabled && !disableRipple) spawnRipple(e.currentTarget, e.clientX, e.clientY);
    onMouseDown?.(e);
  }, [disabled, disableRipple, spawnRipple, onMouseDown]);

  const handleClick = useCallback((e) => {
    if (disabled) return;
    onClick?.(e);
  }, [disabled, onClick]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    if (!disableRipple && (e.key === 'Enter' || e.key === ' ')) spawnRipple(e.currentTarget, null, null);
  }, [disabled, disableRipple, spawnRipple]);

  const handleMouseUp = useCallback((e) => {
    onMouseUp?.(e);
  }, [onMouseUp]);

  return (
    <button
      ref={ref}
      className={className}
      style={inlineStyle}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      type={type}
      title={title}
      disabled={disabled}
      {...rest}
    >
      {!disableRipple && <span className={styles.rippleWrapper} ref={rippleWrapperRef} aria-hidden="true" />}
      {icon ?? children}
    </button>
  );
};

const IconButton = React.forwardRef(IconButtonRender);

IconButton.displayName = 'IconButton';

export default IconButton;
